'use client';

import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { RegularAd } from '@/tracking/ads/components/RegularAd';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import {
  IRelatedNews,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortPathInfo,
  IResortTrailmapPage,
  IWeatherInfo,
  IWeatherItem,
} from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import PlanYourTrip from '@/components/common/PlanYourTrip/ResortWidget';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { Hero400Error } from '@/components/400';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { Heading } from '@/components/ui/heading';
import { useDistanceFormatter, useElevationFormatter, useTemperatureFormatter } from '@/i18n/unitFormat';
import { weatherTypeMap } from '@/util/weatherTypeMap';
import { cn } from '@/util/cn';
import { mpsToKmh } from '@/i18n/units';
import { SectionCard } from '@/components/ui/section-card';
import {
  ElevationKey, ElevationOption, ElevationToggle, pickLevel,
} from '@/page-components/resort/weather/ElevationToggle';
import { WeatherDayRow } from '@/page-components/resort/weather/WeatherDayRow';

export type ResortWeatherPageProps = {
  resort: IResortPageInfo,
  fullResort: IResortPageSnowInfo,
  pathInfo: IResortPathInfo,
  trailmap: IResortTrailmapPage | null,
  weatherInfoDaily: IWeatherInfo | null,
  weatherInfoHourly: IWeatherInfo | null,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-weather-page';
export const TEMPLATE_ID = '2.2.2';
export const TEMPLATE_NAME = 'Resort Weather';

const isoToDayKey = (iso: string) => DateTime.fromISO(iso).toFormat('yyyy-MM-dd');

// Shared class for the desktop column headers. `min-w-0` + `break-words` are
// load-bearing, not cosmetic: grid-cols-12 sizes every track at a fixed 1/12
// (minmax(0, 1fr)), but a grid item defaults to min-width:auto, so a single
// long word that is wider than its track — de "Windgeschwindigkeit" was the
// reported case — paints over the next column instead of wrapping. Any header
// text added here must be able to wrap.
const HEADER_CELL = 'min-w-0 break-words text-xs font-bold uppercase tracking-wider text-secondary';

export default function ResortWeatherPage({
  fullResort,
  pathInfo,
  trailmap,
  weatherInfoDaily,
  weatherInfoHourly,
  relatedNews,
}: ResortWeatherPageProps) {
  const formatTemp = useTemperatureFormatter('temperature');
  const formatDistance = useDistanceFormatter('kmhOrMileh');
  const formatElev = useElevationFormatter();

  if (!fullResort) {
    return <Hero400Error light />;
  }

  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  // Available elevations come from the API: each daily item exposes
  // base/mid/summit slots, optional. Build the toggle list dynamically.
  const firstDay = weatherInfoDaily?.weatherItems?.[0];
  const hasMid = !!firstDay?.mid;
  const hasSummit = !!firstDay?.summit;

  const elevations = useMemo<ElevationOption[]>(() => {
    const list: ElevationOption[] = [
      { key: 'base', label: t('common.base'), ft: trailmap?.elevation?.base },
    ];
    if (hasMid) {
      list.push({ key: 'mid', label: t('common.mid') });
    }
    if (hasSummit) {
      list.push({ key: 'summit', label: t('common.summit'), ft: trailmap?.elevation?.summit });
    }
    return list;
  }, [hasMid, hasSummit, trailmap, t]);

  const [elevation, setElevation] = useState<ElevationKey>('base');

  const dailyItems = weatherInfoDaily?.weatherItems ?? [];
  const hourlyItems = weatherInfoHourly?.weatherItems ?? [];

  // Bucket hourly items by day so an expanded day-row can pull the
  // matching subset cheaply.
  const hourlyByDay = useMemo(() => {
    const map = new Map<string, IWeatherItem[]>();
    hourlyItems.forEach((item) => {
      const key = isoToDayKey(item.datetime);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    });
    return map;
  }, [hourlyItems]);

  // First hourly entry — used for the SEO intro and the "right now"
  // strip above the table.
  const nowItem = hourlyItems[0];
  const nowLevel = pickLevel(nowItem, elevation);
  const nowType = nowLevel?.type;
  const weatherTypeLabel = nowType ? t(weatherTypeMap[nowType]) : '';

  const baseTempStr = formatTemp([nowItem?.base?.temp.min, nowItem?.base?.temp.max], '/', undefined, false, true);
  const summitTempStr = formatTemp([nowItem?.summit?.temp.min, nowItem?.summit?.temp.max], '/', undefined, false, true);
  const windStr = nowItem?.base?.wind.speed
    ? formatDistance(mpsToKmh(nowItem.base.wind.speed), undefined, undefined, true, true)
    : null;

  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Default to the most informative elevation when data first lands.
  // Mid > base > summit, matching the legacy preference order.
  React.useEffect(() => {
    if (hasMid) setElevation('mid');
    else if (hasSummit) setElevation('summit');
    else setElevation('base');
  }, [hasMid, hasSummit]);

  return (
    <>
      <Main>
        <PageBody spacing="md">
          <SeoSection title={t('resort.weather.seoTitle', { resort: fullResort.title })}>
            {m('resort.weather.seoText', {
              resort: fullResort.title,
              wind: windStr,
              weather_type: weatherTypeLabel,
              base_temp: baseTempStr,
              summit_temp: summitTempStr,
            })}
          </SeoSection>

          <ElevationToggle
            options={elevations}
            current={elevation}
            onChange={setElevation}
            formatElev={formatElev}
          />

          {dailyItems.length > 0 ? (
            <SectionCard padding="none" className="overflow-hidden">
              {/* Column header. Mirrors the sm+ grid in WeatherDayRow — keep the
                  col-spans in sync with it. Wind uses the short label the mobile
                  layout already uses, so the narrow track holds it in every locale. */}
              <div className="hidden grid-cols-12 items-start gap-3 border-b border-border bg-muted/40 px-4 py-3 sm:grid">
                <div className={cn('col-span-2', HEADER_CELL)}>{t('resort.weather.date')}</div>
                <div className="col-span-3" aria-hidden />
                <div className={cn('col-span-1', HEADER_CELL)}>{t('resort.weather.high')}</div>
                <div className={cn('col-span-1', HEADER_CELL)}>{t('resort.weather.low')}</div>
                <div className={cn('col-span-2', HEADER_CELL)}>{t('resort.weather.wind')}</div>
                <div className={cn('col-span-2', HEADER_CELL)}>{t('region.skiReport.snowfall')}</div>
                <div className="col-span-1" />
              </div>

              {dailyItems.map((day) => {
                const key = isoToDayKey(day.datetime);
                return (
                  <WeatherDayRow
                    key={key}
                    day={day}
                    hourly={hourlyByDay.get(key) ?? []}
                    elevation={elevation}
                    expanded={expandedKey === key}
                    onToggle={() => setExpandedKey((cur) => (cur === key ? null : key))}
                  />
                );
              })}
            </SectionCard>
          ) : (
            <Heading level={3} className="text-base text-muted-foreground">
              {t('common.na')}
            </Heading>
          )}

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={fullResort.title}
            className="mt-2 mb-7"
          />

          <RegularAd />

          <PlanYourTrip
            resort={pathInfo}
            hasSkiRental={pathInfo.hasSkiRental}
            hasFlights={pathInfo.hasFlights}
            hasCarRental={pathInfo.hasCarRental}
            isPartner={fullResort.isPartner}
            liftsUrl={fullResort.liftsUrl}
            rentalUrl={fullResort.rentalUrl}
            lessonsUrl={fullResort.lessonsUrl}
            resortCountry={fullResort?.region?.country_code}
          />
        </PageBody>
      </Main>

      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd id={pathInfo.uuid} showWeSki={false} />
      </Side>

    </>
  );
}

// fetchProps moved to ./weather.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
